import React, { FC, useEffect, useState } from 'react'
import { Menu } from '@headlessui/react'
import { ArrowDownTrayIcon, EllipsisVerticalIcon, LinkIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, DeleteModal, Tooltip } from '@sistemiv/s-components'
import { useTranslation } from 'react-i18next'
import ProjectService, { Project } from '../../services/ProjectService'
import ColoredProjectFolderIcon from '../../assets/ColoredProjectFolderIcon'
import { useDeleteProject } from '../../repositories/project/mutations/project-delete'
import { useDuplicateProject } from '../../repositories/project/mutations/project-duplicate'
import DuplicateIcon from '../../assets/DuplicateIcon'
import { useRenameProject } from '../../repositories/project/mutations/project-rename'
import AddProjectModal from '../modals/AddProjectModal'
import toast from 'react-hot-toast'
import { saveAs } from 'file-saver'
import { useProjectBulkDelete } from '../../repositories/project/mutations/project-bulk-delete'
import { useProjectBulkDuplicate } from '../../repositories/project/mutations/project-bulk-duplicate'
import classNames from 'classnames'
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'

export type TableHeaders = {
  accessor: string
  label: string
}
export type TableAction = {
  name: string
  icon?: any
  onClick: () => void
}

type TableProps = {
  headers: TableHeaders[]
  rows: Project[]
}

export const formatDate = (stringValue: string) => {
  const date = new Date(stringValue)

  const formattedDate = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  return formattedDate
}

const ProjectsTable: FC<TableProps> = ({ headers, rows }) => {
  const [tableData, setTableData] = useState<Project[]>(rows)
  const { org } = useParams()
  const [projectToEdit, setProjectToEdit] = useState('')
  const [deleteProjectModalOpen, setDeleteProjectModalOpen] = useState(false)
  const [deleteBulkProjectModalOpen, setDeleteBulkProjectModalOpen] = useState(false)
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const { mutate: deleteProject, isLoading: isRemoving } = useDeleteProject()
  const { mutate: deleteBulkProject, isLoading: isBulkRemoving } = useProjectBulkDelete()
  const { mutate: duplicateBulkProject } = useProjectBulkDuplicate()
  const { mutate: renameProject, isLoading: isRenaming } = useRenameProject()
  const { mutate: duplicateProject } = useDuplicateProject()
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState<string[]>([])

  const [sortField, setSortField] = useState('')
  const [order, setOrder] = useState('asc')

  const handleSorting = (sortField: string, sortOrder: string) => {
    if (sortField) {
      const sorted = [...tableData].sort((a, b) => {
        if (a[sortField] === null) return 1
        if (b[sortField] === null) return -1
        if (a[sortField] === null && b[sortField] === null) return 0
        if (a[sortField].type === 'date' && b[sortField].type === 'date') {
          return (dayjs(a[sortField]).isBefore(b[sortField]) ? -1 : 1) * (sortOrder === 'asc' ? 1 : -1)
        }
        return (
          a[sortField].toString().localeCompare(b[sortField].toString(), 'en', { numeric: true }) *
          (sortOrder === 'asc' ? 1 : -1)
        )
      })
      setTableData(sorted)
    }
  }

  const handleSortingChange = (accessor: string) => {
    if (accessor === 'actions') return
    const sortOrder = accessor === sortField && order === 'asc' ? 'desc' : 'asc'
    setSortField(accessor)
    setOrder(sortOrder)
    handleSorting(accessor, sortOrder)
  }

  const handleDelete = () => {
    if (!org || !projectToEdit) return
    deleteProject(
      { organization: org, name: projectToEdit },
      {
        onSuccess: () => {
          setDeleteProjectModalOpen(false)
          setProjectToEdit('')
        },
      },
    )
  }

  const handleRowClick = (e: React.MouseEvent, name: string) => {
    if (e.ctrlKey || e.metaKey) {
      // ctrlKey za Windows/Linux, metaKey za MacOS (cmd)
      setSelectedRows((prevSelected) => {
        if (prevSelected.includes(name)) {
          return prevSelected.filter((n) => n !== name)
        } else {
          return [...prevSelected, name]
        }
      })
    } else {
      // navigate(`/${org}/${name}`)
      return
    }
  }

  const clearSelection = () => {
    setSelectedRows([])
  }

  const handleRename = (newName: string) => {
    if (!org || !projectToEdit) return
    renameProject(
      { organization: org, name: projectToEdit, newName },
      {
        onSuccess: () => {
          setRenameModalOpen(false)
          setProjectToEdit('')
        },
      },
    )
  }

  const handleDuplicate = (name) => {
    if (!org || !name) return
    duplicateProject({ organization: org, name: name })
  }

  const handleBulkDuplicate = () => {
    if (!org || selectedRows.length === 0) return
    duplicateBulkProject({ organization: org, selectedProjects: selectedRows })
  }

  useEffect(() => {
    setTableData(rows)
  }, [rows])

  const handleDownload = (id: string) => {
    ProjectService.downloadProject(org!, id)
      .then((res) => {
        // Proveri da li server šalje ime fajla u Content-Disposition headeru
        const contentDisposition = res.headers['content-disposition']
        let fileName = id + '.zip' // default ime ako server ne pošalje ništa

        if (contentDisposition && contentDisposition.includes('filename=')) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/)
          if (match && match[1]) {
            fileName = match[1]
          }
        }

        if (res.data instanceof Blob) {
          saveAs(res.data, fileName)
        } else {
          const blob = new Blob([res.data])
          saveAs(blob, fileName)
        }
      })
      .catch((err) => {
        console.error('Download error:', err)
      })
  }

  const handleBulkDownload = () => {
    selectedRows.forEach((id) => {
      handleDownload(id)
    })
  }

  const menuActions = (name: string) => {
    return [
      {
        name: t('duplicate'),
        icon: <DuplicateIcon className='w-4 h-4' />,
        onClick: () => {
          if (!org) return
          handleDuplicate(name)
        },
      },
      {
        name: t('delete'),
        icon: <TrashIcon className='w-4 h-4' />,
        onClick: () => {
          setProjectToEdit(name)
          setDeleteProjectModalOpen(true)
        },
      },
    ] as TableAction[]
  }

  const handleBulkCopyLink = () => {
    const links = selectedRows.map((id) => `${window.location.href}/${encodeURIComponent(id)}`).join('\n')
    navigator.clipboard.writeText(links)
    setCopied(true)
    toast.success(t('copiedMsg'))
    setTimeout(() => {
      setCopied(false)
      setProjectToEdit('')
    }, 1000)
  }

  const handleBulkDelete = () => {
    if (!org || selectedRows.length === 0) return
    deleteBulkProject(
      { organization: org, selectedProjects: selectedRows },
      {
        onSuccess: () => {
          setDeleteBulkProjectModalOpen(false)
          setSelectedRows([])
        },
      },
    )
  }

  return (
    <>
      <table className='min-w-full divide-y divide-gray-200 border-collapse'>
        <thead className='text-sm bg-white w-full'>
          <tr className='border-b border-gray-300 w-full'>
            {selectedRows.length > 0 ? (
              <>
                <th colSpan={headers.length + 1} className='flex justify-between items-center w-full'>
                  <div className='px-6 py-3 text-left font-semibold text-gray-500 flex items-center gap-4'>
                    <Button className='!p-0 text-gray-500 hover:text-gray-600' onClick={clearSelection}>
                      ✕
                    </Button>
                    {selectedRows.length} selected
                  </div>
                </th>
                <th></th>
                <th></th>
                <th></th>
                <th>
                  {' '}
                  <div className='flex items-center gap-x-3'>
                    <Tooltip text={t('getLink')}>
                      <Button className='!p-0' onClick={handleBulkCopyLink}>
                        <LinkIcon
                          className={`w-4 h-4 fill-slate-500 hover:fill-gray-800 ${copied ? '!fill-sky-500' : ''}`}
                        />
                      </Button>
                    </Tooltip>
                    <Tooltip text={t('delete')}>
                      <Button
                        className='!p-0'
                        onClick={() => {
                          setDeleteBulkProjectModalOpen(true)
                        }}
                      >
                        <TrashIcon className='w-4 h-4 text-gray-500 hover:text-gray-800' />
                      </Button>
                    </Tooltip>
                    <Tooltip text={t('download')}>
                      <Button className='!p-0' onClick={handleBulkDownload}>
                        <ArrowDownTrayIcon className='w-4 h-4 fill-slate-500 hover:fill-gray-800' />
                      </Button>
                    </Tooltip>
                    <Tooltip text={t('duplicate')}>
                      <Button className='!p-0' onClick={handleBulkDuplicate}>
                        <DuplicateIcon className='w-4 h-4 fill-slate-500 hover:fill-gray-800' />
                      </Button>
                    </Tooltip>
                  </div>
                </th>
              </>
            ) : (
              headers?.map(({ label, accessor }, index) => (
                // <th
                //   data-testid='table-header'
                //   key={index}
                //   scope='col'
                //   className='px-6 py-3 text-left font-normal text-slate-600 capitalize tracking-wider cursor-pointer'
                // >
                //   <div className='inline-flex items-center justify-start gap-x-3'>{header.label}</div>
                // </th>
                <th
                  data-testid={`header-${index}`}
                  key={`header-${index}`}
                  className='text-left p-3 font-normal text-slate-500 cursor-pointer'
                  onClick={() => handleSortingChange(accessor)}
                >
                  <div className='inline-flex items-center justify-start gap-x-3 '>
                    {label}

                    {accessor !== 'actions' &&
                      (order === 'asc' ? (
                        <ArrowDownIcon
                          className={classNames(
                            'h-4 w-4',
                            sortField === accessor ? 'text-slate-500' : 'text-transparent',
                          )}
                        />
                      ) : (
                        <ArrowUpIcon
                          className={classNames(
                            'h-4 w-4 text-slate-500',
                            sortField === accessor ? 'text-slate-500' : 'text-transparent',
                          )}
                        />
                      ))}
                  </div>
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody className='bg-white'>
          {tableData?.map((row, index) => (
            <tr
              key={index}
              className={`border-b border-gray-300 px-3 ${
                selectedRows.includes(row.name) ? 'bg-sky-100' : 'hover:bg-blue-50'
              }`}
              data-testid='row'
              onClick={(e) => handleRowClick(e, row.name)}
            >
              {headers?.map((header, index) => {
                const field = header.accessor === 'action' ? row['name'] : row[header.accessor]
                if (header.accessor === 'actions' && selectedRows.length === 0) {
                  return (
                    <td
                      key={index}
                      className='px-6 py-5 whitespace-nowrap text-sm text-gray-500 text-start'
                      data-testid='table-field'
                    >
                      <div className='flex items-center gap-x-3'>
                        <Tooltip text={t('getLink')}>
                          <Button
                            className='!p-0'
                            onClick={(event) => {
                              event.stopPropagation()
                              navigator.clipboard.writeText(`${window.location.href}/${row['name']}`)
                              setProjectToEdit(row['name'])
                              setCopied(true)
                              toast.success(t('copiedMsg'))
                              setTimeout(() => {
                                setCopied(false)
                                setProjectToEdit('')
                              }, 1000)
                            }}
                          >
                            <LinkIcon
                              className={`w-4 h-4 fill-slate-500 hover:fill-gray-800 ${
                                copied && projectToEdit === row['name'] ? '!fill-sky-500' : ''
                              }`}
                            />
                          </Button>
                        </Tooltip>
                        <Tooltip text={t('rename')}>
                          <Button
                            className='!p-0'
                            onClick={(event) => {
                              event.stopPropagation()
                              setProjectToEdit(row['name'])
                              setRenameModalOpen(true)
                            }}
                          >
                            <PencilIcon className='w-4 h-4 text-gray-500 hover:text-gray-800' />
                          </Button>
                        </Tooltip>
                        <Tooltip text={t('download')}>
                          <Button
                            className='!p-0'
                            onClick={(event) => {
                              event.stopPropagation()
                              handleDownload(row['name'])
                            }}
                          >
                            <ArrowDownTrayIcon className='w-4 h-4 fill-slate-500 hover:fill-gray-800' />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>
                  )
                } else {
                  return (
                    <td
                      key={index}
                      className={`px-6 py-5 whitespace-nowrap text-sm ${
                        header.accessor === 'name' ? 'flex items-center cursor-pointer ' : ''
                      } text-gray-500 text-start`}
                      data-testid='table-field'
                      onClick={(e) =>
                        header.accessor === 'name' && !(e.ctrlKey || e.metaKey) && navigate(`/${org}/${field}`)
                      }
                    >
                      {header.accessor === 'name' && (
                        <>
                          <ColoredProjectFolderIcon className='w-5 h-5 mr-2' /> {field}
                        </>
                      )}
                      {header.accessor === 'lastModified' && formatDate(field)}
                      {header.accessor === 'totalSize' && `${(field / 1024).toFixed(2)} KB`}
                    </td>
                  )
                }
              })}
              {selectedRows.length === 0 ? (
                <td className='p-3'>
                  <Menu as='div' className='relative inline-block text-left'>
                    <div className=''>
                      <Menu.Button
                        className='inline-flex items-center w-full'
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                      >
                        <EllipsisVerticalIcon className='w-6 h-6 text-black hover:text-slate-400' />
                      </Menu.Button>
                    </div>
                    <Menu.Items className='absolute bg-white z-10 right-0 mt-1 w-56 origin-top-right rounded drop-shadow-lg shadow-lg'>
                      <div className='py-1'>
                        {menuActions(row['name'])?.map((action, index) => (
                          <Menu.Item key={`action-${index}`}>
                            <button
                              className='border-none bg-white flex items-center py-2 px-4 gap-x-2 w-full hover:bg-sky-50'
                              onClick={(e) => {
                                e.stopPropagation()
                                action.onClick()
                              }}
                            >
                              {action.icon}
                              {action.name}
                            </button>
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Menu>
                </td>
              ) : (
                <td className='p-3'></td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {deleteProjectModalOpen && (
        <DeleteModal
          title={t('deleteProject')}
          description={t('deleteProjectMsg')}
          open={deleteProjectModalOpen}
          setOpen={setDeleteProjectModalOpen}
          onDeleteConfirmed={handleDelete}
          actionLabel={t('delete')!}
          isLoading={isRemoving}
        />
      )}
      {deleteBulkProjectModalOpen && (
        <DeleteModal
          title={t('deleteProject')}
          description={t('deleteBulkProjectMsg')}
          open={deleteBulkProjectModalOpen}
          setOpen={setDeleteBulkProjectModalOpen}
          onDeleteConfirmed={handleBulkDelete}
          actionLabel={t('delete')!}
          isLoading={isBulkRemoving}
        />
      )}
      {renameModalOpen && (
        <AddProjectModal
          open={renameModalOpen}
          setOpen={setRenameModalOpen}
          onCancel={() => setRenameModalOpen(false)}
          initialValue={projectToEdit}
          onCreate={handleRename}
          creating={isRenaming}
          mode='edit'
        />
      )}
    </>
  )
}

export default ProjectsTable
